import moment from 'moment';
import { select, put, takeLatest, call, delay } from 'redux-saga/effects';
import { putPassword, verifyToken, refreshToken } from 'api';
import { refreshAccessToken } from 'lib/oauth-config';
import {
  AUTH_REFRESH_REQUEST,
  AUTH_SIGNOUT_REQUEST,
  GET_AUTH_STATE,
  CHANGE_PASSWORD_REQUEST,
  AUTH_SIGNIN_REQUEST,
  AUTH_REFRESH_TOKEN,
} from 'redux-api/action/actionTypes';
import * as actions from 'redux-api/action/auth';
import selectAuth from 'redux-api/reselect/auth';

const selectLastRefresh = (state) => state.auth.lastRefresh;

// Swaps an OAuth refresh token for a fresh set, in the shape signInUserSession stores.
function* refreshOAuthSession(token, oauthMetadata) {
  const data = yield call(refreshAccessToken, token);

  return {
    accessToken: data.access_token,
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    oauthMetadata: {
      ...oauthMetadata,
      tokenType: data.token_type,
      expiresIn: data.expires_in,
      scope: data.scope,
      issuedAt: Date.now()
    }
  };
}

// On page load the persisted access token is usually older than its 60 minutes,
// and verify-token refuses it. The refresh token is still good for 30 days, so it
// gets one try before the session is given up: giving up here sends the user to
// /login and through the consent screen again, on every visit after an hour.
function* verifyOrRefresh(session) {
  try {
    const { data } = yield call(verifyToken, session.accessToken, 'signinSaga');
    return { data, session };
  } catch (err) {
    if (!session.oauthMetadata || !session.refreshToken) throw err;

    // eslint-disable-next-line no-console
    console.log('🔄 SigninSaga - access token refused, trying the refresh token');
    const refreshed = yield* refreshOAuthSession(session.refreshToken, session.oauthMetadata);
    const { data } = yield call(verifyToken, refreshed.accessToken, 'signinSaga');
    return { data, session: { ...session, ...refreshed } };
  }
}

function* signinSaga({ payload }) {
  const { pathname, query, ...rest } = payload;
  try {
    // eslint-disable-next-line no-console
    console.log('🔐 SigninSaga - verifying token:', {
      hasAccessToken: !!payload.accessToken,
      hasOAuthMetadata: !!rest.oauthMetadata,
      pathname,
      tokenLength: payload.accessToken?.length,
      tokenPreview: `${payload.accessToken?.substring(0, 20)  }...`,
      timestamp: new Date().toISOString()
    });
    
    const { data, session } = yield* verifyOrRefresh(rest);

    // eslint-disable-next-line no-console
    console.log('✅ SigninSaga - token verified successfully:', {
      hasData: !!data,
      dataKeys: data ? Object.keys(data) : 'no data',
      timestamp: new Date().toISOString()
    });

    // eslint-disable-next-line no-console
    console.log('🚀 SigninSaga - dispatching signinSuccess');
    yield put(actions.signinSuccess({ ...data, signInUserSession: session, pathname, query }));
    
    // eslint-disable-next-line no-console
    console.log('✅ SigninSaga - signinSuccess dispatched');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('❌ SigninSaga - token verification failed:', {
      error: err.message || err,
      status: err.response?.status,
      statusText: err.response?.statusText,
      data: err.response?.data,
      timestamp: new Date().toISOString()
    });
    yield put(actions.signinFailure({ err, pathname }));
  }
}

function* signoutSaga() {
  try {
    // Clear all persistent data from localStorage
    if (typeof window !== 'undefined') {
      try {
        // Clear the persisted Redux state
        localStorage.removeItem('persist:root');
        
        // Clear any other auth-related data
        localStorage.removeItem('aws-amplify-cache');
        localStorage.removeItem('aws-amplify-cache-aws-amplify-cognito');
        
        // Clear sessionStorage as well
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.clear();
        }
        
        // eslint-disable-next-line no-console
        console.log('🧹 Cleared all persistent auth data from storage');
      } catch (storageError) {
        // eslint-disable-next-line no-console
        console.warn('⚠️ Error clearing storage:', storageError);
      }
    }
    
    yield put(actions.signoutSuccess());
  } catch (err) {
    yield put(actions.signinFailure(err));
  }
}

function* getAuthStateSaga({ payload }) {
  try {
    const {
      signInUserSession: { accessToken },
    } = yield select(selectAuth);

    if (!accessToken) {
      yield delay(3000);
      throw new Error('401 - Unauthorized');
    }

    yield call(verifyToken, accessToken, 'getAuthStateSaga');

    yield put(actions.signedSuccess());
  } catch (err) {
    yield put(actions.refreshToken({ pathname: payload.pathname }));
  }
}

function* refreshTokenSaga({ pathname }) {
  try {
    const authState = yield select(selectAuth);
    const { signInUserSession: { refreshToken: token, oauthMetadata } } = authState;

    let data;
    
    // Check if this is an OAuth token refresh
    if (oauthMetadata) {
      data = yield* refreshOAuthSession(token, oauthMetadata);
    } else {
      // Use legacy refresh endpoint
      data = yield call(refreshToken, token);
    }

    yield put(actions.updateToken(data));

    return yield put(actions.signedSuccess());
  } catch (err) {
    return yield put(actions.signedFailure({ err, pathname }));
  }
}

function* refreshAuthSaga() {
  try {
    const now = moment();
    const lastRefresh = yield select(selectLastRefresh);
    const maxRefreshDuration = process.env.REFRESH_TOKEN_DURATION;

    // if (!lastRefresh) return yield put(actions.refreshSkip());

    if (lastRefresh) {
      const diff = now.diff(lastRefresh, 'minutes');
      if (diff < maxRefreshDuration) {
        return yield put(actions.refreshSkip());
      }
    }

    const authState = yield select(selectAuth);
    const { signInUserSession: { refreshToken: token, oauthMetadata } } = authState;

    let data;
    
    // Check if this is an OAuth token refresh
    if (oauthMetadata) {
      data = yield* refreshOAuthSession(token, oauthMetadata);
    } else {
      // Use legacy refresh endpoint
      data = yield call(refreshToken, token);
    }

    yield put(actions.updateToken(data));

    return yield put(actions.refreshSuccess());
  } catch (err) {
    return yield put(actions.refreshFailure(err));
  }
}

function* changePasswordSaga({ payload: { currentPassword, password, callback } }) {
  try {
    const { signInUserSession, adminRequestedStreetlibInternalId } = yield select(selectAuth);

    // The token is the operator's own under impersonation, so this would change the
    // operator's password, not the customer's. The form is disabled then too; this
    // is the second lock, for anything that dispatches the action another way.
    if (adminRequestedStreetlibInternalId) {
      yield put(actions.changePasswordFailure({ message: 'Use StreetLib OS to change a customer password.' }));
      return;
    }

    const { accessToken } = signInUserSession;

    yield call(putPassword, accessToken, { current_password: currentPassword, new_password: password });

    yield put(actions.changePasswordSuccess());
    callback();
  } catch (err) {
    if (err.response) {
      if (err.response.data.errors) {
        // dash-api answers { errors: [{ detail }] } and the form shows `message`.
        const [first] = err.response.data.errors;
        yield put(actions.changePasswordFailure({ ...first, message: first.message || first.detail || first.title }));
      } else {
        yield put(actions.changePasswordFailure(err.response.data));
      }
    } else {
      yield put(actions.changePasswordFailure(err));
    }
  }
}

export default function* watchAuth() {
  yield takeLatest(AUTH_REFRESH_TOKEN, refreshTokenSaga);
  yield takeLatest(AUTH_REFRESH_REQUEST, refreshAuthSaga);
  yield takeLatest(AUTH_SIGNIN_REQUEST, signinSaga);
  yield takeLatest(AUTH_SIGNOUT_REQUEST, signoutSaga);
  yield takeLatest(GET_AUTH_STATE, getAuthStateSaga);
  yield takeLatest(CHANGE_PASSWORD_REQUEST, changePasswordSaga);
}